import chevronIconSVG from "../../icons/chevron-down.svg?raw";
import AnchorPositionObserver from "../interfaces/AnchorPositionObserver";
import {
  assignAttributes,
  closest,
  createElement as h,
  getAttributes,
  replaceChildren,
  uniqueId,
  contains,
} from "../utils";

/**
 * A popover component.
 *
 * @example ```html
 * <!-- Default usage -->
 * <ifrs-popover>
 *   <details>
 *     <summary>Popover button text</summary>
 *     Popover content. Lorem ipsum...
 *   </details>
 * </ifrs-popover>
 * ```
 */
export default class Popover extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-popover") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  constructor() {
    super();
    this.cleanup = new Set();
    this.positionObserver = new AnchorPositionObserver();
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup;

  /**
   * The current state of the component.
   *
   * @type {boolean}
   */
  get open() {
    return this.hasAttribute("open");
  }

  /**
   * @param {boolean} value The new state of the component.
   */
  set open(value) {
    this.toggleAttribute("open", !!value);
  }

  /** @type {AnchorPositionObserver} */
  positionObserver;

  /**
   * Reacts to changes of observed attributes. The function makes DOM updates
   * to reflect the current state of the component.
   *
   * @param {string} name The changed attribute name
   * @param {*} prev The previous value of the attribute
   * @param {*} next The new value of the attribute
   */
  attributeChangedCallback(name, prev, next) {
    // Toggle component state when the `open` attribute changes.
    if (name === "open") {
      const button = this.querySelector(".popover-button");
      const content = this.querySelector(".popover-content");

      // Abort if we don't have the expected markup.
      if (
        !(button instanceof HTMLElement) ||
        !(content instanceof HTMLElement)
      ) {
        return;
      }

      // Get the new state of the component.
      const isOpen = next !== null;

      // Set the `aria-expanded` attribute.
      button.setAttribute("aria-expanded", isOpen.toString());

      if (content instanceof HTMLDialogElement) {
        // Open/close the dialog element
        if (isOpen) {
          content.showModal();
        } else {
          content.close();
        }
      } else {
        // Toggle the content element visibility
        content.hidden = !isOpen;
      }

      if (isOpen) {
        this.positionObserver.observe(button, content);
      } else {
        this.positionObserver.disconnect();
      }
    }
  }

  /**
   * Set up the component once it has been added to the DOM.
   */
  connectedCallback() {
    // Exit early if the component is not connected.
    if (!this.isConnected) {
      return;
    }

    // Set up component markup
    this.setup();

    // Rerun setup when component children change.
    const observer = new MutationObserver(() => this.setup());
    observer.observe(this, { childList: true });
    this.cleanup.add(() => observer.disconnect());

    /**
     * Toggle the component state on click events.
     *
     * @param {PointerEvent} event A PointerEvent object.
     */
    const handleClick = (event) => {
      if (!contains(this, event.target)) {
        // Close the component on "outside" click events
        this.open = false;
      } else if (closest(event.target, ".popover-button")) {
        // Toggle the component state button click events
        this.open = !this.open;
      } else if (event.target instanceof HTMLDialogElement) {
        // Close the component on dialog backdrop clicks
        this.open = false;
      }
    };

    document.addEventListener("click", handleClick);
    this.cleanup.add(() => document.removeEventListener("click", handleClick));

    const dialog = this.querySelector("dialog");

    if (dialog) {
      /**
       * Sync component state on native dialog close events
       */
      const handleDialogClose = () => (this.open = false);

      dialog.addEventListener("close", handleDialogClose);
      this.cleanup.add(() =>
        dialog.addEventListener("close", handleDialogClose),
      );
    }

    if (!dialog) {
      /**
       * Close the component when it loses focus
       *
       * @param {FocusEvent} event
       */
      const handleFocusOut = (event) =>
        contains(this, event.relatedTarget) || (this.open = false);

      this.addEventListener("focusout", handleFocusOut);
      this.cleanup.add(() =>
        this.removeEventListener("focusout", handleFocusOut),
      );

      /**
       * Close the component with the Escape key
       *
       * @param {KeyboardEvent} event
       */
      const handleKeyDown = (event) =>
        event.key === "Escape" && (this.open = false);

      document.addEventListener("keydown", handleKeyDown);
      this.cleanup.add(() =>
        document.removeEventListener("keydown", handleKeyDown),
      );
    }
  }

  /**
   * Clean up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Set up the component markup.
   *
   * Note that this method may be called repeatedly and should be idempotent.
   */
  setup() {
    // Abort if the component markup has already been set up.
    if (
      this.children.length > 0 &&
      this.firstElementChild.matches(".popover-button")
    ) {
      return;
    }

    // Get the first descendant `<details>` element.
    const details = this.querySelector("details");

    // Abort if no child details element exists.
    if (!details) {
      return;
    }

    // Copy attributes from the details element.
    assignAttributes(this, getAttributes(details));

    // Get the descendant `<summary>` element.
    let summary = details.querySelector("summary");

    if (summary) {
      // Remove the summary element if it exists. This lets us easily reference
      // all other child nodes of the details element.
      summary.remove();
    } else {
      // Create a default summary element if none exists.
      summary = h("summary", { textContent: "Details" });
    }

    // Create a unique id for aria purposes.
    const id = uniqueId(this.tagName);

    // Get and set the current component state.
    const isOpen = this.hasAttribute("open") || details.open;
    this.toggleAttribute("open", isOpen);

    /**
     * Create the component trigger button.
     */
    const button = h(
      "button",
      {
        ...getAttributes(summary),
        "aria-controls": id,
        "aria-expanded": isOpen ? "true" : "false",
        id: `${id}-button`,
        className: `${summary.className} popover-button`.trim(),
        type: "button",
      },
      summary.childNodes,
    );

    if (!button.querySelector(".popover-icon")) {
      // The trigger icon
      button.append(
        h(chevronIconSVG, {
          "aria-hidden": "true",
          classList: "icon popover-icon",
          xmlns: null,
        }),
      );
    }

    /**
     * Create the component content container elements.
     */

    const content =
      this.hasAttribute("modal") && isDialogSupported()
        ? h("dialog", { className: "popover-content", id }, details.childNodes)
        : h(
            "div",
            { className: "popover-content", hidden: true, id },
            details.childNodes,
          );

    // Replace the component content with our new markup.
    replaceChildren(this, button, content);
  }
}

Object.assign(Popover, { observedAttributes: ["open"] });

function isDialogSupported() {
  try {
    return typeof document.createElement("dialog").showModal === "function";
  } catch (error) {
    // Ignore
  }
  return false;
}
